import { HttpClient, HttpHeaders } from '@angular/common/http';

import { AppConfigSettings } from '@arc/arc-shared-frontend';
import { Observable } from 'rxjs';
import {
  BtPayContext,
  CreatePaymentAccountRequestV1,
  CreatePaymentAccountResponseV1,
  GetAllPaymentAccountsResponseV1,
  UpdatePaymentAccountExpirationDateRequestV1,
} from '../models';
import { PaymentAccountsService } from './payment-accounts.service';

export class PaymentAccountsPlatformService extends PaymentAccountsService {
  protected baseUrl: string;

  constructor(
    private httpClient: HttpClient,
    protected appConfig: AppConfigSettings,
  ) {
    super(appConfig);
  }

  public createPaymentAccountV1(
    tenantId: string,
    createAccountRequest: CreatePaymentAccountRequestV1,
    sendNotifications: boolean = false,
  ) {
    let url = `${this.baseUrl}/v1/tenants/${tenantId}/payment-accounts`;
    if (sendNotifications) {
      url = url + `?sendNotifications=${sendNotifications}`;
    }

    return this.httpClient.post<CreatePaymentAccountResponseV1>(
      url,
      createAccountRequest,
    );
  }

  public deletePaymentAccount(
    tenantId: string,
    paymentAccountIdentifierBurn: string,
    btPayContext: BtPayContext,
  ): Observable<void> {
    let customHeaders = new HttpHeaders();
    customHeaders = customHeaders.append(
      'X-BTPAY-CONTEXT',
      JSON.stringify(btPayContext),
    );
    const endcodedPayAcctBurn = encodeURIComponent(
      paymentAccountIdentifierBurn,
    );
    return this.httpClient.delete<void>(
      `${this.baseUrl}/v1/tenants/${tenantId}/payment-accounts/${endcodedPayAcctBurn}`,
      { headers: customHeaders },
    );
  }

  public getAllPaymentAccountsV1(
    tenantId: string,
    buyerAccount: string,
    btPayContext: BtPayContext,
  ): Observable<GetAllPaymentAccountsResponseV1> {
    let customHeaders = new HttpHeaders();
    customHeaders = customHeaders.append(
      'X-BTPAY-CONTEXT',
      JSON.stringify(btPayContext),
    );

    const normalizedBuyerAccount = buyerAccount?.startsWith('accountNumber:')
      ? buyerAccount
      : `accountNumber:${buyerAccount}`;

    return this.httpClient.get<GetAllPaymentAccountsResponseV1>(
      `${this.baseUrl}/v1/tenants/${tenantId}/accounts/${encodeURIComponent(
        normalizedBuyerAccount,
      )}/payment-accounts`,
      { headers: customHeaders },
    );
  }

  public getAllPaymentAccountsV1ByUsers(
    tenantId: string,
    buyerAccount: string,
    btPayContext: BtPayContext,
    externalUserUrn: string,
  ): Observable<GetAllPaymentAccountsResponseV1> {
    let customHeaders = new HttpHeaders();
    customHeaders = customHeaders.append(
      'X-BTPAY-CONTEXT',
      JSON.stringify(btPayContext),
    );

    const normalizedBuyerAccount = buyerAccount?.startsWith('accountNumber:')
      ? buyerAccount
      : `accountNumber:${buyerAccount}`;

    return this.httpClient.get<GetAllPaymentAccountsResponseV1>(
      `${this.baseUrl}/v1/tenants/${tenantId}/accounts/${encodeURIComponent(
        normalizedBuyerAccount,
      )}/users/${encodeURIComponent(externalUserUrn)}/payment-accounts`,
      { headers: customHeaders },
    );
  }

  public updateExpirationDateV1(
    tenantId: string,
    paymentAccountBurn: string,
    updateExpirationRequest: UpdatePaymentAccountExpirationDateRequestV1,
  ): Observable<void> {
    const encodedPayAcctBurn = encodeURIComponent(paymentAccountBurn);
    return this.httpClient.put<void>(
      `${this.baseUrl}/v1/tenants/${tenantId}/payment-accounts/${encodedPayAcctBurn}/expiration-date`,
      updateExpirationRequest,
    );
  }
}
